/**
 * BulkConfirmModal — modal de confirmación reutilizable para acciones bulk
 * sin form complejo (hide / dudoso / aprobar / forzar re-cataloga).
 *
 * Estilo basado en HideReasonModal / RecatalogConfirmModal (sin Radix).
 */
import React, { useState } from 'react';
import { X, Loader2, AlertTriangle } from 'lucide-react';

export type BulkActionKind = 'hide' | 'dubious' | 'approve' | 'recataloga';

interface Props {
  isOpen: boolean;
  action: BulkActionKind;
  count: number;
  /** Texto del título (i18n key resuelto por el caller). */
  title: string;
  /** Descripción larga (incluye copy de afectación tomas pasadas/futuras). */
  description: string;
  confirmLabel: string;
  cancelLabel: string;
  commentPlaceholder: string;
  onClose: () => void;
  onConfirm: (comment: string) => Promise<void>;
}

const ACTION_STYLES: Record<BulkActionKind, { icon: string; btn: string; iconColor: string }> = {
  hide: {
    icon: 'text-red-600',
    btn: 'bg-red-600 hover:bg-red-700 text-white',
    iconColor: 'text-red-600',
  },
  dubious: {
    icon: 'text-amber-600',
    btn: 'bg-amber-600 hover:bg-amber-700 text-white',
    iconColor: 'text-amber-600',
  },
  approve: {
    icon: 'text-emerald-600',
    btn: 'bg-emerald-600 hover:bg-emerald-700 text-white',
    iconColor: 'text-emerald-600',
  },
  recataloga: {
    icon: 'text-violet-600',
    btn: 'bg-violet-600 hover:bg-violet-700 text-white',
    iconColor: 'text-violet-600',
  },
};

const BulkConfirmModal: React.FC<Props> = ({
  isOpen,
  action,
  count,
  title,
  description,
  confirmLabel,
  cancelLabel,
  commentPlaceholder,
  onClose,
  onConfirm,
}) => {
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  if (!isOpen) return null;

  const styles = ACTION_STYLES[action];

  const handleConfirm = async () => {
    setSubmitting(true);
    setErr(null);
    try {
      await onConfirm(comment);
      setComment('');
      onClose();
    } catch (e: unknown) {
      const msg = (e as { message?: string })?.message || 'Error desconocido';
      setErr(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-lg max-w-md w-full mx-4 shadow-xl border border-gray-200">
        {/* Header */}
        <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <AlertTriangle className={`w-5 h-5 ${styles.iconColor}`} />
            <h3 className="text-base font-semibold text-gray-900">{title}</h3>
          </div>
          <button
            onClick={onClose}
            disabled={submitting}
            className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50"
            aria-label="Cerrar"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="px-4 py-3 space-y-3 text-sm">
          <p className="text-sm text-gray-700">{description}</p>

          <div className="bg-gray-50 border border-gray-200 rounded px-3 py-2 text-xs">
            <span className="text-gray-500">Items afectados: </span>
            <span className="tabular-nums font-semibold text-gray-900">
              {count.toLocaleString('es-AR')}
            </span>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Comentario (opcional)
            </label>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              disabled={submitting}
              rows={2}
              placeholder={commentPlaceholder}
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>

          {err && (
            <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2">
              {err}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            disabled={submitting}
            className="px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100 rounded disabled:opacity-50"
          >
            {cancelLabel}
          </button>
          <button
            onClick={handleConfirm}
            disabled={submitting}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded disabled:opacity-50 ${styles.btn}`}
          >
            {submitting && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BulkConfirmModal;
